import React, { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  BrowserRouter as Router,
} from "react-router-dom";
import logo from "./logo.svg";
import "./App.css";
import { IConfig } from "./types/IConfig";
import Spinner from "./components/Spinner/Spinner";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";

// Eager: small, on nearly every route (guards / layouts / auth).
import { PrivateRoute } from "./pages/PrivateRoute/PrivateRoute";
import { AuthProvider } from "./utility/useAuth";
import { AccessProvider } from "./access/AccessProvider";
import { DefaultLayout } from "./layouts/Default";
import GuestLayout from "./layouts/Guest";
// Eager, and a SIBLING of <Suspense> below — see the comment at its mount point.
import SupportAssistantHost from "./components/SupportAssistant/SupportAssistantHost";
import ErrorDialogHost from "./components/Modals/ErrorModal/ErrorDialogHost";
import AccessDeniedNotice from "./access/AccessDeniedNotice";
import { CapabilityRoute } from "./access/CapabilityRoute";
import { isAnalyticsEnabled } from "./utility/analyticsAccess";

// Route screens are code-split so each ships as its own chunk — the initial
// bundle no longer pulls in every page's Syncfusion imports. The
// webpackChunkName comments name the output files (e.g. page-chart.<hash>.chunk.js)
// so the bundle-size report is readable; see scripts/check-bundle-size.js.
const Home = lazy(() => import(/* webpackChunkName: "page-home" */ "./pages/Home"));
const Login = lazy(() => import(/* webpackChunkName: "page-login" */ "./pages/Login"));
const Logout = lazy(() => import(/* webpackChunkName: "page-logout" */ "./pages/Logout"));
const JobPage = lazy(() => import(/* webpackChunkName: "page-job" */ "./pages/JobPage"));
const RoutePage = lazy(() => import(/* webpackChunkName: "page-routing" */ "./pages/RoutingPage"));
const BomPage = lazy(() => import(/* webpackChunkName: "page-bom" */ "./pages/BomPage"));
const JobImagesPage = lazy(() => import(/* webpackChunkName: "page-job-images" */ "./pages/ImagesPage/JobImagesPage"));
const SimilarJobsImagesPage = lazy(() => import(/* webpackChunkName: "page-similar-jobs-images" */ "./pages/SimilarJobsImages"));
const ImagesPage = lazy(() => import(/* webpackChunkName: "page-images" */ "./pages/ImagesPage/ImagesPage"));
const DocumentsPage = lazy(() => import(/* webpackChunkName: "page-documents" */ "./pages/ImagesPage/DocumentsPage"));
const SystemPage = lazy(() => import(/* webpackChunkName: "page-system" */ "./pages/SystemPage/SystemPage"));
const OperationsPage = lazy(() => import(/* webpackChunkName: "page-operations" */ "./pages/OperationsPage/OperationsPage"));
const UsersPage = lazy(() => import(/* webpackChunkName: "page-users" */ "./pages/UsersPage/UsersPage"));
const ToolsetsPage = lazy(() => import(/* webpackChunkName: "page-toolsets" */ "./pages/ToolsetsPage"));
const JobDocumentsPage = lazy(() => import(/* webpackChunkName: "page-job-documents" */ "./pages/JobDocuments/JobDocumentsPage"));
const DiagnosticPage = lazy(() => import(/* webpackChunkName: "page-diagnostic" */ "./pages/DiagnosticPage/DiagnosticPage"));
const HierarchicalPage = lazy(() => import(/* webpackChunkName: "page-hierarchical" */ "./pages/HierarchicalPage/HierarchicalPage"));
const ReportPage = lazy(() => import(/* webpackChunkName: "page-report" */ "./pages/ReportPage/ReportPage"));
const TACPage = lazy(() => import(/* webpackChunkName: "page-terms-and-conditions" */ "./pages/TACPage/TACPage"));
const OrgChangePage = lazy(() => import(/* webpackChunkName: "page-org-change" */ "./pages/OrgChangePage/OrgChangePage"));
const OptOutPage = lazy(() => import(/* webpackChunkName: "page-opt-out" */ "./pages/OptOutPage/OptOutPage"));


/**
 * Paths of discontinued screens, redirected to the home board. See the route list below.
 *
 * `/Toolset` and `/Calibrations` stay here although Tools and Toolsets is a live screen again:
 * that screen is `/toolsets`, and an old bookmark to either of these still has to land on the
 * board. The router matches a path ignoring case, so `/Toolset` also takes `/toolset`, which is
 * why the live route is spelled with an "s" and must never be shortened.
 */
const DISCONTINUED_PATHS: readonly string[] = [
  "/chart", "/allocations", "/tool", "/CompareBOM", "/CapacitySettings", "/TestResults",
  "/Calibrations", "/Toolset", "/Certifications",
  "third-party-visual-inspection/:sequence?", "cnc-route-card-visual-inspection/:sequence?",
  "factory-acceptance-inspection/:sequence?", "packaging-inspection/:sequence?",
  "barcode/:sequenceNumber",
];

// Merge our Window interface into the global scope
declare global {
  interface Window {
    config: IConfig;
  }
}

const RoutesWrap: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <App />
      </Router>
    </ErrorBoundary>
  );
};

// Role-gated routes are wrapped in <CapabilityRoute capability="…">, which reads what the user
// may do from GET api/me (access/CapabilityRoute.tsx). Never the role bits in the store: the
// auth cache keeps that record for 30 minutes.

// --- Analytics feature-flag wrapper (Key Vault secret AnalyticsEnabled = "Y") ---
// Guards direct navigation / bookmarks to /Analytics when the feature is off; the
// header entry point (CSMShopfloorHeader) is hidden by the same flag.
const AnalyticsRoute: React.FC<React.PropsWithChildren> = ({ children }) => {
  if (!isAnalyticsEnabled()) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <div className="App">
      {/* <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        <p>
          Edit <code>src/App.tsx</code> and save to reload.
        </p>
        <a
          className="App-link"
          href="https://reactjs.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn React
        </a>
      </header> */}
      <AuthProvider authConfig={null}>
        {/* What the user may do, read from GET api/me. ABOVE <Suspense> and around both hosts
            below: a lazy screen loading replaces everything under the boundary, which would
            drop the answer and read it again on every first visit to a screen. Inside
            <AuthProvider>, because it calls the BFF with the user's token. */}
        <AccessProvider>
        <Suspense fallback={<Spinner />}>
        <ReactRoutes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Navigate to="/home" />
              </PrivateRoute>
            }
          />
          <Route
            path="/login"
            element={
              <GuestLayout>
                <Login />
              </GuestLayout>
            }
          />
          <Route
            path="/logout"
            element={
              <GuestLayout>
                <Logout />
              </GuestLayout>
            }
          />
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <Home />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="job/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="termsandconditions"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <TACPage />
                </DefaultLayout>
              </PrivateRoute>
            }
            
          />
          <Route
  path="choose-organization"
  element={
    <PrivateRoute>
      <CapabilityRoute capability="switchOrganization">
        <DefaultLayout>
          <OrgChangePage />
        </DefaultLayout>
      </CapabilityRoute>
    </PrivateRoute>
  }
/>


          <Route
            path="consent-details"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <OptOutPage />
                </DefaultLayout>
              </PrivateRoute>
            }
            />
           <Route
            path="log/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <RoutePage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />          
          <Route
            path="bom/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <BomPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/images"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <ImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <DocumentsPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />

          {/* --------- MANAGER ROUTES (each needs its own capability) --------- */}
          <Route
            path="/system"
            element={
              <PrivateRoute>
                <CapabilityRoute capability="manageSettings">
                  <DefaultLayout>
                    <SystemPage />
                  </DefaultLayout>
                </CapabilityRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <CapabilityRoute capability="viewUsers">
                  <DefaultLayout>
                    <UsersPage />
                  </DefaultLayout>
                </CapabilityRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/operations"
            element={
              <PrivateRoute>
                <CapabilityRoute capability="manageOperations">
                  <DefaultLayout>
                    <OperationsPage />
                  </DefaultLayout>
                </CapabilityRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/toolsets"
            element={
              <PrivateRoute>
                <CapabilityRoute capability="viewTools">
                  <DefaultLayout>
                    <ToolsetsPage />
                  </DefaultLayout>
                </CapabilityRoute>
              </PrivateRoute>
            }
          />
          {/* --------- END MANAGER ROUTES --------- */}

          <Route
            path="/diagnostic"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <DiagnosticPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/hierarchical"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <HierarchicalPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/Analytics"
            element={
              <PrivateRoute>
                <AnalyticsRoute>
                  <DefaultLayout>
                    <ReportPage />
                  </DefaultLayout>
                </AnalyticsRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="job-images/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="similar-jobs-images/"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <SimilarJobsImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="job-documents/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobDocumentsPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          {/* DISCONTINUED SCREENS (2026-09-28). The barcode page, the four inspection reports and the
              manager planning/quality screens are no longer part of CSM. Their old paths go to the
              home board rather than matching nothing, which would render an empty page. The page
              components are no longer imported; the shop-floor board's barcode SEARCH is a
              different feature and is unaffected. */}
          {DISCONTINUED_PATHS.map((path) => (
            <Route key={path} path={path} element={<Navigate to="/home" replace />} />
          ))}
        </ReactRoutes>
        </Suspense>

        {/* ⚠️ A SIBLING OF <Suspense>, NOT A CHILD OF IT, AND THAT PLACEMENT IS THE WHOLE POINT.
            Every screen above is React.lazy behind that ONE boundary, so the first navigation
            to an unvisited route suspends and REPLACES the entire subtree with the spinner —
            unmounting anything inside it. The support assistant used to be mounted down in
            layouts/Default, so following a link the assistant itself had just offered destroyed
            the panel and the conversation in it. It survived the second visit because lazy()
            caches, which is why the bug presented as "open-from-chat sometimes loses my chat".

            Still inside <AuthProvider> and <Router>: it calls the BFF with the user's token and
            it reads and changes the route. Do not move it, and do not make it lazy. */}
        <SupportAssistantHost />

        {/* ⚠️ A SIBLING OF <Suspense> FOR THE SAME REASON, AND A SIBLING OF THE LINE ABOVE FOR
            A DIFFERENT ONE. Under the boundary, a dialog would be unmounted mid-read by the
            first navigation to an unvisited route. Inside SupportAssistantHost, it would
            inherit that host's suppressions — /login, /logout, /diagnostic/* and "terms not
            accepted yet" — which are screens where HELP is not offered, not screens where
            failures stop happening. A failed sign-in is exactly a failure a user needs to be
            able to read. See components/Modals/ErrorModal/ErrorDialogHost. */}
        <ErrorDialogHost />

        {/* Says so when the server refuses a call for a capability the user does not have.
            Outside <Suspense> for the same reason as the dialog host: a navigation to an
            unvisited screen must not take it away while it is being read. */}
        <AccessDeniedNotice />
        </AccessProvider>
      </AuthProvider>
    </div>
  );
}

//export default App;

export default RoutesWrap;